// Dashboard (staff home): headline numbers, assessments by month (this year against last), by visit type and by
// organization type, and a calendar of testing events. Charts are plain SVG (no chart library), so they print too.
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ErrorBox } from '../components/Layout.tsx';
import { orgMatches } from '../lib/orgSearch.ts';
import { useData } from '../lib/useData.ts';
import { STAGE_LABEL } from '../lib/eventStatus.ts';

interface Dash {
  year: number; today: string;
  months: { month: number; count: number; lastYear: number }[];
  byType: { type: string; n: number }[];
  byOrgType: { category: string; n: number; orgs: number }[];
  filter: { departmentId: string | null; departmentName: string | null; category: string | null };
  totals: { year: number; lastYear: number; ytd: number; lastYtd: number; awaitingPhysician: number; drafts: number; upcomingEvents: number };
}
interface OrgRow { id: string; name: string; category: string | null; archived: boolean; n: number; lastYear: number; people: number }
/** What the whole dashboard is narrowed to: one organization, or one organization type. */
interface Focus { departmentId?: string; departmentName?: string; category?: string }
interface CalEvent { id: string; startsOn: string; endsOn: string; location: string | null; status: string; stage: string; eventType: string | null; team: string | null; departmentName: string; category: string | null; assessments: number;
  otherOrganizations?: string[] }
const withOthers = (e: CalEvent) => (e.otherOrganizations?.length ? `${e.departmentName} + ${e.otherOrganizations.join(', ')}` : e.departmentName);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TYPE_LABEL: Record<string, string> = { physical: 'Physical', cohsg: 'COHSG screening', labs: 'Labs only' };
const TYPE_COLOR: Record<string, string> = { physical: '#8B0000', cohsg: '#2f6f8f', labs: '#b08a2e' };
const fmt = (n: number) => n.toLocaleString('en-US');

export function Dashboard() {
  const [year, setYear] = useState<number | null>(null);
  const [focus, setFocus] = useState<Focus>({});
  const qs = new URLSearchParams();
  if (year) qs.set('year', String(year));
  if (focus.departmentId) qs.set('departmentId', focus.departmentId);
  if (focus.category) qs.set('category', focus.category);
  const { data, error } = useData<Dash>(`/api/dashboard${qs.size ? `?${qs}` : ''}`);
  const pick = (f: Focus) => { setFocus(f); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  if (error) return <ErrorBox error={error} />;
  if (!data) return <p className="muted">Loading…</p>;
  const t = data.totals;
  const thisYear = Number(data.today.slice(0, 4));
  const change = t.lastYtd ? Math.round(((t.ytd - t.lastYtd) / t.lastYtd) * 100) : null;
  return (
    <div className="dash">
      <div className="spread">
        <h1 style={{ margin: 0 }}>Dashboard</h1>
        <div className="dash-year">
          <button className="secondary small" onClick={() => setYear(data.year - 1)} aria-label="Previous year">‹</button>
          <strong>{data.year}</strong>
          <button className="secondary small" disabled={data.year >= thisYear} onClick={() => setYear(data.year + 1)} aria-label="Next year">›</button>
        </div>
      </div>

      {(focus.departmentId || focus.category) && (
        <div className="dash-focus">
          Showing <b>{focus.departmentName ?? focus.category}</b>{focus.departmentId ? ' only' : ' organizations'}
          {focus.departmentId && <> · <Link to={`/organizations/${focus.departmentId}`}>open the organization</Link></>}
          <button type="button" className="secondary small" onClick={() => setFocus({})}>✕ Show all</button>
        </div>)}

      <div className="dash-tiles">
        <Tile label={data.year === thisYear ? 'Assessments this year' : `Assessments in ${data.year}`} value={fmt(t.ytd)}
          note={change === null ? undefined : `${change >= 0 ? '+' : ''}${change}% vs. same point in ${data.year - 1}`} tone={change !== null && change < 0 ? 'down' : 'up'} />
        <Tile label="Awaiting physician" value={fmt(t.awaitingPhysician)} to="/queue" note="in the sign queue" />
        <Tile label="In progress" value={fmt(t.drafts)} to="/assessments" note="drafts and iPad captures" />
        <Tile label="Events, next 30 days" value={fmt(t.upcomingEvents)} to="/events" note="scheduled or under way" />
      </div>

      <div className="dash-grid">
        <section className="card dash-wide">
          <div className="spread"><h2 style={{ margin: 0 }}>Assessments by month</h2>
            <span className="small muted"><span className="key" style={{ background: '#8B0000' }} />{data.year} · <span className="key" style={{ background: '#c9c3b5' }} />{data.year - 1}</span></div>
          <MonthChart months={data.months} />
          <p className="small muted" style={{ margin: 0 }}>{data.year}: {fmt(t.year)} · {data.year - 1}: {fmt(t.lastYear)}</p>
        </section>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>By visit type</h2>
          <Donut parts={data.byType.map((x) => ({ label: TYPE_LABEL[x.type] ?? x.type, n: x.n, color: TYPE_COLOR[x.type] ?? '#888' }))} />
        </section>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>By organization type</h2>
          <HBars rows={data.byOrgType.map((x) => ({ label: x.category, n: x.n, note: `${fmt(x.orgs)} org${x.orgs === 1 ? '' : 's'}` }))}
            onPick={focus.departmentId || focus.category ? undefined : (label) => label !== 'No organization' && pick({ category: label })} />
          {!focus.departmentId && !focus.category && <p className="small muted" style={{ margin: '0.4rem 0 0' }}>Click a type to show only those organizations.</p>}
        </section>

        <section className="card dash-wide">
          <EventCalendar today={data.today} departmentId={focus.departmentId} />
        </section>

        {!focus.departmentId && (
          <section className="card dash-wide">
            <OrgChart year={data.year} category={focus.category} onPick={(o) => pick({ departmentId: o.id, departmentName: o.name })} />
          </section>)}
      </div>
    </div>
  );
}

function Tile({ label, value, note, to, tone }: { label: string; value: string; note?: string; to?: string; tone?: 'up' | 'down' }) {
  const body = <><div className="dash-tile-label">{label}</div><div className="dash-tile-value">{value}</div>
    {note && <div className={`dash-tile-note${tone ? ` ${tone}` : ''}`}>{note}</div>}</>;
  return to ? <Link to={to} className="dash-tile">{body}</Link> : <div className="dash-tile">{body}</div>;
}

/** Twelve months: this year's bars, last year's beside them (lighter). */
function MonthChart({ months }: { months: Dash['months'] }) {
  const W = 760, H = 240, padL = 44, padB = 26, padT = 10;
  const max = Math.max(1, ...months.flatMap((m) => [m.count, m.lastYear]));
  const step = niceStep(max);
  const top = Math.ceil(max / step) * step;
  const slot = (W - padL) / 12, bw = slot * 0.34;
  const y = (v: number) => padT + (H - padT - padB) * (1 - v / top);
  const ticks = Array.from({ length: Math.floor(top / step) + 1 }, (_, i) => i * step);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="dash-chart" role="img" aria-label="Assessments by month, this year and last year">
      {ticks.map((v) => <g key={v}><line x1={padL} x2={W} y1={y(v)} y2={y(v)} className="grid" /><text x={padL - 6} y={y(v) + 4} textAnchor="end" className="axis">{fmt(v)}</text></g>)}
      {months.map((m, i) => {
        const x = padL + i * slot + slot / 2;
        return (
          <g key={m.month}>
            <rect x={x - bw - 1} y={y(m.lastYear)} width={bw} height={y(0) - y(m.lastYear)} fill="#c9c3b5"><title>{MONTHS[i]} last year: {fmt(m.lastYear)}</title></rect>
            <rect x={x + 1} y={y(m.count)} width={bw} height={y(0) - y(m.count)} fill="#8B0000"><title>{MONTHS[i]}: {fmt(m.count)}</title></rect>
            {m.count > 0 && <text x={x + 1 + bw / 2} y={y(m.count) - 4} textAnchor="middle" className="val">{m.count >= 1000 ? `${(m.count / 1000).toFixed(1)}k` : m.count}</text>}
            <text x={x} y={H - 8} textAnchor="middle" className="axis">{MONTHS[i]}</text>
          </g>);
      })}
    </svg>
  );
}
function niceStep(max: number) {
  const raw = max / 4, p = 10 ** Math.floor(Math.log10(raw)), f = raw / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * p;
}

function Donut({ parts }: { parts: { label: string; n: number; color: string }[] }) {
  const total = parts.reduce((s, p) => s + p.n, 0);
  if (!total) return <p className="muted small">No assessments in this year.</p>;
  const R = 70, r = 44, C = 90;
  let a0 = -Math.PI / 2;
  const arcs = parts.map((p) => {
    const a1 = a0 + (p.n / total) * Math.PI * 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const pt = (rad: number, a: number) => `${C + rad * Math.cos(a)} ${C + rad * Math.sin(a)}`;
    // A full circle (one type only) is drawn as two halves.
    const d = p.n === total
      ? `M ${pt(R, -Math.PI / 2)} A ${R} ${R} 0 1 1 ${pt(R, Math.PI / 2)} A ${R} ${R} 0 1 1 ${pt(R, -Math.PI / 2)} M ${pt(r, -Math.PI / 2)} A ${r} ${r} 0 1 0 ${pt(r, Math.PI / 2)} A ${r} ${r} 0 1 0 ${pt(r, -Math.PI / 2)} Z`
      : `M ${pt(R, a0)} A ${R} ${R} 0 ${large} 1 ${pt(R, a1)} L ${pt(r, a1)} A ${r} ${r} 0 ${large} 0 ${pt(r, a0)} Z`;
    a0 = a1;
    return { ...p, d };
  });
  return (
    <div className="dash-donut">
      <svg viewBox="0 0 180 180" role="img" aria-label="Assessments by visit type">
        {arcs.map((p) => <path key={p.label} d={p.d} fill={p.color} fillRule="evenodd"><title>{p.label}: {fmt(p.n)}</title></path>)}
        <text x="90" y="88" textAnchor="middle" className="donut-total">{fmt(total)}</text>
        <text x="90" y="106" textAnchor="middle" className="axis">assessments</text>
      </svg>
      <ul className="dash-legend">{parts.map((p) => (
        <li key={p.label}><span className="key" style={{ background: p.color }} />{p.label}<b>{fmt(p.n)}</b><span className="muted">{Math.round((p.n / total) * 100)}%</span></li>))}</ul>
    </div>
  );
}

function HBars({ rows, onPick }: { rows: { label: string; n: number; note: string }[]; onPick?: (label: string) => void }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  const total = rows.reduce((s, r) => s + r.n, 0);
  if (!total) return <p className="muted small">No assessments in this year.</p>;
  return (
    <div className="dash-hbars">{rows.map((r) => (
      <div key={r.label} className={`dash-hbar${onPick ? ' pickable' : ''}`} title={`${r.label}: ${fmt(r.n)} assessments (${r.note})`}
        onClick={onPick ? () => onPick(r.label) : undefined} role={onPick ? 'button' : undefined} tabIndex={onPick ? 0 : undefined}
        onKeyDown={onPick ? (e) => { if (e.key === 'Enter') onPick(r.label); } : undefined}>
        <div className="dash-hbar-label">{r.label}</div>
        <div className="dash-hbar-track"><div className="dash-hbar-fill" style={{ width: `${(r.n / max) * 100}%` }} /></div>
        <div className="dash-hbar-n">{fmt(r.n)} <span className="muted">{Math.round((r.n / total) * 100)}%</span></div>
      </div>))}
    </div>
  );
}

// ------------------------------------------------------------------ by organization
const SHOW = [10, 25, 50, 0] as const;   // 0 = all

/** Assessments by organization in the year, biggest first, with last year's count. Filter by name, organization type and
 *  visit type; click an organization to narrow the whole dashboard to it. */
function OrgChart({ year, category, onPick }: { year: number; category?: string; onPick: (o: OrgRow) => void }) {
  const [type, setType] = useState('');
  const [visit, setVisit] = useState('');
  const [q, setQ] = useState('');
  const [show, setShow] = useState<number>(10);
  const cat = category ?? type;
  const qs = new URLSearchParams({ year: String(year) });
  if (cat) qs.set('category', cat);
  if (visit) qs.set('visitType', visit);
  const { data, error } = useData<OrgRow[]>(`/api/dashboard/organizations?${qs}`);
  const all = useData<{ category: string }[]>(`/api/dashboard/organizations?year=${year}`);   // for the type list
  const types = useMemo(() => [...new Set((all.data ?? []).map((o) => o.category || 'Not categorized'))].sort(), [all.data]);
  const rows = useMemo(() => (data ?? []).filter((o) => !q.trim() || orgMatches(o.name, q)), [data, q]);
  const shown = show ? rows.slice(0, show) : rows;
  const max = Math.max(1, ...shown.map((o) => Math.max(o.n, o.lastYear)));
  const total = rows.reduce((t, o) => t + o.n, 0);
  return (
    <>
      <div className="spread"><h2 style={{ margin: 0 }}>By organization</h2>
        <span className="small muted">{data ? `${fmt(rows.length)} organization${rows.length === 1 ? '' : 's'} · ${fmt(total)} assessments in ${year}` : ''}</span></div>
      <div className="dash-filters">
        <input type="search" placeholder="Search organizations…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search organizations" />
        {!category && <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Organization type">
          <option value="">All types</option>{types.map((t) => <option key={t} value={t}>{t}</option>)}</select>}
        <select value={visit} onChange={(e) => setVisit(e.target.value)} aria-label="Visit type">
          <option value="">All visit types</option>{Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <select value={show} onChange={(e) => setShow(Number(e.target.value))} aria-label="How many">
          {SHOW.map((n) => <option key={n} value={n}>{n ? `Top ${n}` : 'All'}</option>)}</select>
      </div>
      <ErrorBox error={error} />
      {!data ? <p className="muted small">Loading…</p> : !shown.length ? <p className="muted small">No organizations match.</p> : (
        <div className="dash-orgs">{shown.map((o, i) => (
          <div key={o.id} className="dash-org" role="button" tabIndex={0} onClick={() => onPick(o)} onKeyDown={(e) => { if (e.key === 'Enter') onPick(o); }}
            title={`${o.name}: ${fmt(o.n)} assessments of ${fmt(o.people)} people in ${year}; ${fmt(o.lastYear)} in ${year - 1}. Click to show only this organization.`}>
            <div className="dash-org-rank">{i + 1}</div>
            <div className="dash-org-name">{o.name}<span className="muted small"> · {o.category || 'Not categorized'}{o.archived ? ' · archived' : ''}</span></div>
            <div className="dash-org-bars">
              <div className="dash-org-bar now" style={{ width: `${(o.n / max) * 100}%` }} />
              <div className="dash-org-bar last" style={{ width: `${(o.lastYear / max) * 100}%` }} />
            </div>
            <div className="dash-org-n"><b>{fmt(o.n)}</b> <span className="muted small">{o.lastYear ? `${o.n >= o.lastYear ? '+' : ''}${fmt(o.n - o.lastYear)}` : 'new'}</span></div>
          </div>))}
        </div>)}
      {show > 0 && rows.length > show && <button type="button" className="link small" onClick={() => setShow(0)}>Show all {fmt(rows.length)}</button>}
      <p className="small muted" style={{ margin: '0.4rem 0 0' }}>Red: {year} · grey: {year - 1} · the number after the count is the change from {year - 1}. Click an organization to see only its numbers, months and events.</p>
    </>
  );
}

// ------------------------------------------------------------------ calendar
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const STATUS_LABEL: Record<string, string> = STAGE_LABEL;

/** A month of testing events (weeks start Sunday). Click an event to open it; the list below shows the month's events in order. */
function EventCalendar({ today, departmentId }: { today: string; departmentId?: string }) {
  const [month, setMonth] = useState(() => new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 1, 1));
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = new Date(first); gridStart.setDate(1 - first.getDay());
  const days = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  const weeks = days[35]!.getMonth() !== month.getMonth() && days[28]!.getMonth() !== month.getMonth() ? 5 : 6;
  const shown = days.slice(0, weeks * 7);
  const from = iso(shown[0]!), to = iso(shown[shown.length - 1]!);
  const { data, error } = useData<CalEvent[]>(`/api/dashboard/events?from=${from}&to=${to}${departmentId ? `&departmentId=${departmentId}` : ''}`);
  const nav = useNavigate();
  const byDay = useMemo(() => {
    const m = new Map<string, CalEvent[]>();
    for (const e of data ?? []) {
      const d = new Date(Number(e.startsOn.slice(0, 4)), Number(e.startsOn.slice(5, 7)) - 1, Number(e.startsOn.slice(8, 10)));
      const end = e.endsOn;
      for (let k = 0; k < 31 && iso(d) <= end; k++, d.setDate(d.getDate() + 1)) {
        const key = iso(d); if (!m.has(key)) m.set(key, []); m.get(key)!.push(e);
      }
    }
    return m;
  }, [data]);
  const inMonth = (data ?? []).filter((e) => e.endsOn >= iso(first) && e.startsOn <= iso(new Date(month.getFullYear(), month.getMonth() + 1, 0)));
  const go = (n: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  return (
    <>
      <div className="spread">
        <h2 style={{ margin: 0 }}>Testing events</h2>
        <div className="dash-year">
          <button className="secondary small" onClick={() => go(-1)} aria-label="Previous month">‹</button>
          <strong style={{ minWidth: 130, textAlign: 'center' }}>{month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</strong>
          <button className="secondary small" onClick={() => go(1)} aria-label="Next month">›</button>
          <button className="secondary small" onClick={() => setMonth(new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 1, 1))}>Today</button>
          <Link className="small" to="/events">All events</Link>
        </div>
      </div>
      <ErrorBox error={error} />
      <div className="cal">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d} className="cal-head">{d}</div>)}
        {shown.map((d) => {
          const key = iso(d), evs = byDay.get(key) ?? [];
          return (
            <div key={key} className={`cal-day${d.getMonth() !== month.getMonth() ? ' other' : ''}${key === today ? ' today' : ''}`}>
              <div className="cal-num">{d.getDate()}</div>
              {evs.slice(0, 3).map((e) => (
                <button key={e.id} type="button" className={`cal-ev ${e.stage}`} onClick={() => nav(`/events/${e.id}`)}
                  title={`${withOthers(e)}${e.location ? ` · ${e.location}` : ''} · ${STATUS_LABEL[e.stage] ?? e.stage} · ${e.assessments} assessed`}>
                  {e.departmentName}{e.otherOrganizations?.length ? ` +${e.otherOrganizations.length}` : ''}</button>))}
              {evs.length > 3 && <div className="cal-more">+{evs.length - 3} more</div>}
            </div>);
        })}
      </div>
      <div className="small muted" style={{ marginTop: 6 }}>
        {(['scheduled', 'active', 'awaiting_results', 'finalizing', 'completed', 'postponed'] as const).map((k) => <span key={k}><span className={`key cal-key ${k}`} />{STAGE_LABEL[k]} </span>)}
      </div>
      {inMonth.length > 0 && (
        <ul className="cal-list">{inMonth.map((e) => (
          <li key={e.id}><Link to={`/events/${e.id}`}>{e.startsOn === e.endsOn ? e.startsOn : `${e.startsOn} – ${e.endsOn}`} · <b>{withOthers(e)}</b></Link>
            {e.location ? ` · ${e.location}` : ''} <span className="muted">· {STATUS_LABEL[e.stage] ?? e.stage}{e.assessments ? ` · ${e.assessments} assessed` : ''}</span></li>))}
        </ul>)}
      {data && !inMonth.length && <p className="muted small">No testing events this month.</p>}
    </>
  );
}
